/**
 * 出行救援服务 caseType（source_id）映射与剩余次数文案工具
 * 对照《App端案件权益与一键救援接口文档》2.3
 */

/**
 * 默认 caseType（931 系列，泰和君安等产品常用）
 * @type {Record<string, string>}
 */
const DEFAULT_CASE_TYPE_MAP = Object.freeze({
  urgent: '931',
  medicalReturn: '932',
  medicalGuardianship: '933',
  butlerservice: '934',
  inpatientCollaboration: '939',
  admissionEscort: '940',
  outpatientAssist: '941',
  expeditedExamination: '942',
  secondOpinion: '944',
  multidisciplinaryConsultation: '945',
  overseasMedicalArrangement: '946',
  globalMedicineSearch: '947',
  postDischargeCareGuidance: '948',
  postMedicalFollowUp: '949'
})

/**
 * 按 productId 覆盖 caseType（同名权益不同产品可能对应不同 source_id / productConfigId）
 * 按权益配置 name → productConfigId 作为 caseType
 * @type {Record<string, Record<string, string>>}
 */
const PRODUCT_CASE_TYPE_OVERRIDE = Object.freeze({
  '951': {
    // 全球救援服务医疗转运
    urgent: '953',
    // 全球救援服务医疗送返
    medicalReturn: '954',
    // 住院协助
    inpatientCollaboration: '955',
    // 门诊协助
    outpatientAssist: '956',
    // 检查加急
    expeditedExamination: '957',
    // 入院陪护
    admissionEscort: '959',
    // 二次诊疗
    secondOpinion: '960',
    // 多学科会诊
    multidisciplinaryConsultation: '961',
    // 海外就医安排
    overseasMedicalArrangement: '962',
    // 全球找药
    globalMedicineSearch: '963',
    // 院后康复指导
    postDischargeCareGuidance: '964',
    // 医后随访
    postMedicalFollowUp: '965'
  },
  '1040': {
    // 全球救援服务医疗转运
    urgent: '953',
    // 全球救援服务医疗送返
    medicalReturn: '954',
    // 全球安全管家服务
    butlerservice: '934',
    // 住院协助
    inpatientCollaboration: '955',
    // 门诊协助
    outpatientAssist: '956',
    // 检查加急
    expeditedExamination: '957',
    // 二次诊疗
    secondOpinion: '960',
    // 多学科会诊
    multidisciplinaryConsultation: '945',
    // 海外就医安排
    overseasMedicalArrangement: '946',
    // 全球找药
    globalMedicineSearch: '963',
    // 院后康复指导
    postDischargeCareGuidance: '964',
    // 医后随访
    postMedicalFollowUp: '965'
  },
  '949': {
    // 全球紧急救援（含紧急医疗转运、医疗送返）
    urgent: '938',
    medicalReturn: '938',
    // 住院协助
    inpatientCollaboration: '939',
    // 入院陪护
    admissionEscort: '940',
    // 门诊协助
    outpatientAssist: '941',
    // 检查加急
    expeditedExamination: '942',
    // 二次诊疗
    secondOpinion: '944',
    // 多学科会诊
    multidisciplinaryConsultation: '945',
    // 海外就医安排
    overseasMedicalArrangement: '946',
    // 全球找药
    globalMedicineSearch: '947',
    // 院后康复指导
    postDischargeCareGuidance: '948',
    // 医后随访
    postMedicalFollowUp: '949'
  },
  '947': {
    // 紧急医疗转运
    urgent: '931',
    // 医疗送返
    medicalReturn: '932',
    // 住院医疗监护
    medicalGuardianship: '933',
    // 全球安全管家服务
    butlerservice: '934',
    // 门诊协助
    outpatientAssist: '790'
  },
  '843': {
    // 门诊协助（该产品配置中与出行救援二级页对应的项）
    outpatientAssist: '790'
  },
  '830': {
    // 门诊绿通 → 门诊协助
    outpatientAssist: '750',
    // 就医陪诊服务 → 入院陪护
    admissionEscort: '751',
    // 检查绿通 → 检查加急
    expeditedExamination: '752',
    // 住院绿通 → 住院协助
    inpatientCollaboration: '753',
    // 随访管理计划 → 医后随访
    postMedicalFollowUp: '758'
  },
  '614': {
    // 门诊协助
    outpatientAssist: '283'
  }
})

/**
 * 根据 productId + 服务标识解析 caseType
 * @param {string|number|null|undefined} productId - 路由 query.productId
 * @param {string} serviceKey - 服务标识，如 urgent、medicalReturn
 * @returns {string} caseType 字符串，未匹配时返回空字符串
 */
export function getServiceCaseType(productId, serviceKey) {
  const key = String(serviceKey || '').trim()
  if (!key) return ''
  const idStr = productId != null && productId !== '' ? String(productId).trim() : ''
  if (idStr && PRODUCT_CASE_TYPE_OVERRIDE[idStr] && PRODUCT_CASE_TYPE_OVERRIDE[idStr][key]) {
    return PRODUCT_CASE_TYPE_OVERRIDE[idStr][key]
  }
  return DEFAULT_CASE_TYPE_MAP[key] || ''
}

/**
 * 将计量类型转为数字（兼容接口返回字符串）
 * @param {unknown} billingType
 * @returns {number|null}
 */
export function normalizeBillingType(billingType) {
  if (billingType === null || billingType === undefined || billingType === '') return null
  const n = Number(billingType)
  return Number.isFinite(n) ? n : null
}

/**
 * 计算剩余次数（计次类型，或有 timesCount 时）
 * @param {{ billingType?: number|string|null, timesCount?: number|string|null, usedCount?: number|string|null }|null|undefined} info
 * @returns {number|null} 剩余次数；无总量时返回 null
 */
export function calcRemainCount(info) {
  if (!info) return null
  const total = Number(info.timesCount)
  if (!Number.isFinite(total) || total < 0) return null
  const used = Number(info.usedCount)
  const usedSafe = Number.isFinite(used) && used > 0 ? used : 0
  return Math.max(0, total - usedSafe)
}

/**
 * 是否为「不限次」或「限额/额度」类展示文案（不展示剩余次数）
 * @param {string} display
 * @returns {boolean}
 */
function isUnlimitedOrQuotaText(display) {
  const text = String(display || '').trim()
  if (!text) return false
  if (text.includes('不限')) return true
  if (text.includes('限额')) return true
  // 如「100万」「200万元」等额度描述（非「N次」）
  if (/万/.test(text) && !/次/.test(text)) return true
  return false
}

/** sessionStorage：二级页跳转一键救援时暂存权益统计，供救援接口透传 */
export const RESCUE_EQUITY_STORAGE_KEY = 'rescueEquityUsedInfo'

/**
 * 权益统计字段名（与 equity-used-count 响应 / rescue 入参一致）
 * @type {readonly string[]}
 */
const EQUITY_PAYLOAD_KEYS = Object.freeze([
  'usedCount',
  'billingType',
  'timesCount',
  'feeAmount',
  'billingText',
  'billingDisplay',
  'canUse'
])

/**
 * 从权益统计结果中组装一键救援透传字段（仅非空写入）
 * @param {Object|null|undefined} info - equity-used-count 的 data
 * @returns {Object}
 */
export function buildRescueEquityPayload(info) {
  if (!info || typeof info !== 'object') return {}
  /** @type {Record<string, *>} */
  const out = {}
  for (let i = 0; i < EQUITY_PAYLOAD_KEYS.length; i++) {
    const key = EQUITY_PAYLOAD_KEYS[i]
    const val = info[key]
    if (val === null || val === undefined || val === '') continue
    out[key] = val
  }
  return out
}

/**
 * 暂存权益统计，供 onClickRescue 提交时透传
 * @param {Object|null|undefined} info
 * @returns {void}
 */
export function saveRescueEquityInfo(info) {
  try {
    if (!info) {
      sessionStorage.removeItem(RESCUE_EQUITY_STORAGE_KEY)
      return
    }
    sessionStorage.setItem(RESCUE_EQUITY_STORAGE_KEY, JSON.stringify(info))
  } catch (e) {
    console.warn('[saveRescueEquityInfo] 写入失败', e)
  }
}

/**
 * 读取并可选清除暂存的权益统计
 * @param {{ clear?: boolean }} [options] - clear 为 true 时读取后清除，默认不清除
 * @returns {Object|null}
 */
export function loadRescueEquityInfo(options = {}) {
  try {
    const raw = sessionStorage.getItem(RESCUE_EQUITY_STORAGE_KEY)
    if (!raw) return null
    if (options.clear === true) {
      sessionStorage.removeItem(RESCUE_EQUITY_STORAGE_KEY)
    }
    return JSON.parse(raw)
  } catch (e) {
    console.warn('[loadRescueEquityInfo] 读取失败', e)
    return null
  }
}

/**
 * 清除暂存的权益统计
 * @returns {void}
 */
export function clearRescueEquityInfo() {
  try {
    sessionStorage.removeItem(RESCUE_EQUITY_STORAGE_KEY)
  } catch (e) {
    console.warn('[clearRescueEquityInfo] 清除失败', e)
  }
}

/**
 * 组装「获取服务」按钮文案（仅计次展示剩余次数）
 * 不限次、限额（如限额200万元 / 100万）等不展示后缀
 * @param {{ billingType?: number|string|null, timesCount?: number|string|null, usedCount?: number|string|null, billingDisplay?: string|null, billingText?: string|null }|null|undefined} info
 * @param {string} [baseText='获取服务']
 * @returns {string}
 */
export function formatServiceBtnText(info, baseText = '获取服务') {
  if (!info) return baseText

  const billingType = normalizeBillingType(info.billingType)
  const display = (info.billingDisplay || info.billingText || '').toString().trim()

  // 计费 / 文本计量：不展示剩余次数
  if (billingType === 2 || billingType === 3) {
    return baseText
  }
  // 文案为不限次、限额、XX万：不展示剩余次数
  if (isUnlimitedOrQuotaText(display)) {
    return baseText
  }

  // 仅计次类型展示剩余次数
  if (billingType === 1) {
    const remain = calcRemainCount(info)
    if (remain != null) {
      return `${baseText}（剩余${remain}次）`
    }
  }

  return baseText
}



// WEBPACK FOOTER //
// ./src/utils/equityCaseType.js